import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import Button from '../../../ui/Button';
import useFormRefs from './useFormRefs';
import { useValidator } from '../../../hooks/formValidator/useValidator';
import { useFocusOnFirstErrorField } from '../../../hooks/errorFieldFocus';
import FormFields from './formFields';
import useEditorFormState from './useFormState';
import useEditorValidationSchema from './useValidationSchema';
import { generateUpdatePayload, handleSubmitError, validateUpdatedFormFields } from '../helpers';
import { addEditor, addEditorPerInstitution, updateEditor, } from '../../../api/admin/editors';
import { generateIdRoutePath } from '../../../helpers';
import Loader from '../../../ui/Loader';
const EditorForm = ({ formType, editorId, institutionId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { formState, updatedFormFields, setUpdatedFormFields, isStateReady } = useEditorFormState(editorId);
    const { inputsValidationSchema } = useEditorValidationSchema({
        password: formState.password,
        formType,
    });
    const { formErrors, setFormErrors, validateAll, validateInputField } = useValidator({
        inputSchema: inputsValidationSchema,
    });
    const { refs, orderedRefs } = useFormRefs();
    const [submitStatus, setSubmitStatus] = useState(null);
    const [forceFocus, setForceFocus] = useState(false);
    useFocusOnFirstErrorField(formErrors, orderedRefs, forceFocus, setForceFocus);
    const returnToEditorsList = () => institutionId
        ? navigate(generateIdRoutePath(APP_ROUTES.editorsPerInstitution, institutionId))
        : navigate(APP_ROUTES.editors);
    const handleCreateEditor = async () => {
        const errors = validateAll({
            inputValues: {
                firstName: formState.firstName,
                lastName: formState.lastName,
                email: formState.email,
                password: formState.password,
                confirmPassword: formState.confirmPassword,
            },
        });
        if (Object.keys(errors).length > 0) {
            setForceFocus(true);
            return;
        }
        setSubmitStatus('pending');
        const payload = {
            firstName: formState.firstName,
            lastName: formState.lastName,
            email: formState.email,
            isActive: formState.isActive,
            password: formState.password,
        };
        try {
            if (institutionId) {
                await addEditorPerInstitution(institutionId, payload);
            }
            else {
                await addEditor(payload);
            }
            setSubmitStatus('success');
            returnToEditorsList();
        }
        catch (error) {
            handleSubmitError({ error, setSubmitStatus, setFormErrors, setForceFocus });
        }
    };
    const handleEditEditor = async () => {
        if (updatedFormFields.size) {
            const updatedFieldsHasErrors = validateUpdatedFormFields({
                updatedFormFields,
                validateInputField,
                formState,
                unit: 'editor',
            });
            if (updatedFieldsHasErrors) {
                setForceFocus(true);
                return;
            }
            setSubmitStatus('pending');
            try {
                await updateEditor(editorId, generateUpdatePayload({
                    formState,
                    updatedFormFields,
                    unit: 'editor',
                }));
                setSubmitStatus('success');
                returnToEditorsList();
            }
            catch (error) {
                handleSubmitError({ error, setSubmitStatus, setFormErrors, setForceFocus });
            }
        }
    };
    return (_jsx("form", { className: 'super-admin-form', onSubmit: (event) => event.preventDefault(), children: isStateReady ? (_jsxs(_Fragment, { children: [_jsx(FormFields, { refs: refs, formState: formState, formErrors: formErrors, validateInputField: validateInputField, setUpdatedFormFields: setUpdatedFormFields, formType: formType }), _jsx("div", { className: "super-admin-form-submit", children: _jsxs(ButtonsGroup, { children: [_jsx(Button, { type: "submit", onClick: formType === 'create' ? handleCreateEditor : handleEditEditor, label: t(formType === 'create' ? 'button.create_editor' : 'button.edit_editor'), disabled: submitStatus === 'pending' ||
                                    (formType === 'edit' && updatedFormFields.size === 0) }), _jsx(Button, { variant: 'outlined', onClick: returnToEditorsList, label: t('button.cancel') })] }) })] })) : (_jsx(Loader, {})) }));
};
export default EditorForm;
